/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable react/no-this-in-sfc */
import { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  subDays,
  endOfDay,
  isSameDay,
  differenceInCalendarDays,
  startOfDay,
  isBefore,
  parseISO,
  addDays,
} from 'date-fns';
import { BOOKING_DATE_START_DATE } from 'modules/common/constants/date-range';
import { InputAdornment } from '@mui/material';
import { ClickAwayListener } from '@mui/base';
import { DefinedRange, DateRange } from 'react-date-range';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import { TextField } from './style';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { selectStayDatesPresetList } from '../../../users/selectors';
import variants from '../../theme/variants';
import { selectAppTheme, selectLastFullBookingDate } from '../../../dashboard/selectors';

/**
 * Custom booking date range picker common component that uses across modules for select booking date range
 * @param {Function} handleChange - function for setting data range
 * @param {Array} ranges - start date and end data of the picker
 * @param {String} label - textfield label
 * @param {String} adornment - whether we need to adornment is beginning or not
 * @param {String} dateValue - selected date value
 * @param {String} cssClassName - use style for entire component
 * @param {String} maxDate - maximum date what we need to define
 * @param {String} targetDate - stay date of the component
 * @param {String} dataFeedDate - latest booking date of the hotel
 * @returns
 */
const CustomBookingDateRangePicker = (props) => {
  const {
    handleChange,
    ranges,
    label,
    adornment,
    dateValue,
    cssClassName,
    maxDate,
    targetDate,
    dataFeedDate,
    id = '',
  } = props;
  //
  const presetList = useSelector(selectStayDatesPresetList);
  const themeName = useSelector(selectAppTheme);
  const lastFullBookingDate = useSelector(selectLastFullBookingDate);
  const [open, setOpen] = useState(false);
  const [formattedPresetList, setFormattedPresetList] = useState([]);
  //
  const selectedVariant = variants.find((variant) => variant.name === themeName);
  //
  const refTarget = useRef(null);
  //
  const customBookingPresets = useMemo(
    () =>
      lastFullBookingDate
        ? [
            {
              label: 'Last Full Day',
              range: () => ({
                startDate: startOfDay(parseISO(lastFullBookingDate)),
                endDate: startOfDay(parseISO(lastFullBookingDate)),
              }),
              isSelected(range) {
                const definedRange = this.range();
                return (
                  isSameDay(range.startDate, definedRange.startDate) &&
                  isSameDay(range.endDate, definedRange.endDate)
                );
              },
            },
            {
              label: 'Last Full 7 Days',
              range: () => ({
                startDate: addDays(startOfDay(parseISO(lastFullBookingDate)), -6),
                endDate: startOfDay(parseISO(lastFullBookingDate)),
              }),
              isSelected(range) {
                const definedRange = this.range();
                return (
                  isSameDay(range.startDate, definedRange.startDate) &&
                  isSameDay(range.endDate, definedRange.endDate)
                );
              },
            },
          ]
        : [],
    [lastFullBookingDate]
  );
  //
  useEffect(() => {
    if (presetList?.length > 0) {
      const formattedSet = presetList
        ?.filter(
          (set) =>
            isBefore(startOfDay(new Date(set?.startDate)), startOfDay(new Date())) &&
            isBefore(startOfDay(new Date(set?.endDate)), startOfDay(new Date()))
        )
        ?.map((set) => ({
          label: set?.presetName,
          range: () => ({
            startDate: startOfDay(new Date(set?.startDate)),
            endDate: endOfDay(new Date(set?.endDate)),
          }),
          isSelected(range) {
            const definedRange = this.range();
            return (
              isSameDay(range.startDate, definedRange.startDate) &&
              isSameDay(range.endDate, definedRange.endDate)
            );
          },
        }));
      setFormattedPresetList(formattedSet);
    }
  }, [presetList]);
  //
  let dateRange;
  if (ranges) {
    dateRange = [
      {
        startDate: new Date(ranges[0].startDate),
        endDate: new Date(ranges[0].endDate),
        key: 'selection',
        color: selectedVariant?.palette.primary.main,
      },
    ];
  } else {
    dateRange = [
      {
        startDate: '',
        endDate: '',
        key: 'selection',
        color: selectedVariant?.palette.primary.main,
      },
    ];
  }
  //
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className="calendarWrap">
        <TextField
          id="booking date range picker"
          InputProps={{
            startAdornment: <InputAdornment position="start">{adornment}</InputAdornment>,
          }}
          label={label}
          value={dateValue}
          size="small"
          color="primary"
          onClick={() => setOpen(!open)}
          sx={{
            '& .MuiOutlinedInput-input': {
              fontSize: 12,
            },
            '& .MuiOutlinedInput-root': {
              height: 'fit-content',
              padding: 0,
            },
          }}
          className="booking-font"
        />
        <div ref={refTarget}>
          {open && (
            <>
              <DefinedRange
                onChange={handleChange}
                ranges={dateRange}
                className={styles.bookingDefinedDateElement}
                inputRanges={[
                  {
                    label: 'Trailing days',
                    range: (value) => ({
                      startDate: subDays(new Date(targetDate.endDate), value),
                      endDate: subDays(new Date(targetDate.endDate), 1),
                    }),
                    getCurrentValue(range) {
                      if (!range.startDate) return '∞';
                      return differenceInCalendarDays(
                        new Date(targetDate.endDate),
                        range.startDate
                      );
                    },
                  },
                  {
                    label: 'Last Days',
                    range: (value) => ({
                      startDate: subDays(new Date(dataFeedDate), value),
                      endDate: subDays(new Date(dataFeedDate), 1),
                    }),
                    getCurrentValue(range) {
                      if (!range.startDate) return '∞';
                      return differenceInCalendarDays(new Date(dataFeedDate), range.startDate);
                    },
                  },
                ]}
                staticRanges={[
                  {
                    label: 'Last Day',
                    range: () => ({
                      startDate: endOfDay(new Date(dataFeedDate)),
                      endDate: endOfDay(new Date(dataFeedDate)),
                    }),
                    isSelected(range) {
                      const definedRange = this.range();
                      return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                      );
                    },
                  },
                  {
                    label: 'Last 7 Days',
                    range: () => ({
                      startDate: subDays(endOfDay(new Date(dataFeedDate)), 6),
                      endDate: endOfDay(new Date(dataFeedDate)),
                    }),
                    isSelected(range) {
                      const definedRange = this.range();
                      return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                      );
                    },
                  },
                  {
                    label: 'Last 14 Days',
                    range: () => ({
                      startDate: subDays(endOfDay(new Date(dataFeedDate)), 13),
                      endDate: endOfDay(new Date(dataFeedDate)),
                    }),
                    isSelected(range) {
                      const definedRange = this.range();
                      return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                      );
                    },
                  },
                  ...customBookingPresets,
                  ...formattedPresetList,
                ]}
              />
              <DateRange
                id={id}
                onChange={handleChange}
                editableDateInputs
                moveRangeOnFirstSelection={false}
                ranges={dateRange}
                months={1}
                direction="horizontal"
                color="#fff"
                className={cssClassName}
                maxDate={maxDate}
                minDate={new Date(BOOKING_DATE_START_DATE)}
                dateDisplayFormat="yyyy-MM-dd"
              />
            </>
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
};
//
export default CustomBookingDateRangePicker;
